import { Component, inject, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Netzzuordnung } from '../../../domain/model/netzzuordnung';
import { NetzzuordnungService } from '../../../service/netzzuordnung/netzzuordnung.service';
import { lastValueFrom, ReplaySubject } from 'rxjs';
import { AbstractForm } from '../abstract-form.model';
import { FormGroupConfig } from '../form-config.model';
import { MatValidators } from '../../../common/material/components/mat-form-field-required.directive';
import { ErzeugungsanlageConfig } from '../erzeugungsanlage.config';
import { Feld } from '../../../domain/model/feld.enum';

@Component({
    selector: 'redispatch-netzzuordnung-sub-form',
    templateUrl: './netzzuordnung-sub-form.component.html',
    styleUrls: [
        './netzzuordnung-sub-form.component.scss',
    ],
    standalone: false,
})
export class NetzzuordnungSubFormComponent extends AbstractForm<Netzzuordnung> implements OnInit {

    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;

    public recentNetzzuordnung: Netzzuordnung = null;
    public selectedNetzzuordnung: Netzzuordnung = null;
    public netzzuordnungInitialized$: ReplaySubject<void> = new ReplaySubject<void>();

    public ortsnamenSelection: string[] = [];
    public stationsNamenSelection: string[] = [];
    public trafoFeldNamenSelection: string[] = [];

    public readonly Feld = Feld;

    @Input({ required: true }) public stadtwerkeId: string;
    @Input({ required: true }) readonly: boolean;

    protected netzzuordnungService = inject(NetzzuordnungService);

    constructor() {
        super(inject(UntypedFormBuilder));
    }

    get formGroupConfig(): FormGroupConfig<Netzzuordnung> {
        return {
            ortsname: [
                { value: '', disabled: true },
                MatValidators.required,
            ],
            stationsname: [
                { value: '', disabled: true },
                MatValidators.required,
            ],
            trafoFeldName: [
                { value: '', disabled: true },
                MatValidators.required,
            ],
        } as unknown as FormGroupConfig<Netzzuordnung>; // Needed to ignore missing/backend values like uuid, kaskadenId ...
    }

    ngOnInit() {
        this.recentNetzzuordnung = {} as Netzzuordnung;
        this.initializeNetzzuordnung();
    }

    public sanitize(netzzuordnung: Netzzuordnung): Netzzuordnung {
        if (this.selectedNetzzuordnung) {
            return this.selectedNetzzuordnung;
        }
        return netzzuordnung;
    }

    updateDisabledStates() {
        if (this.readonly) {
            this.f.ortsname.disable();
            this.f.stationsname.disable();
            this.f.trafoFeldName.disable();
        } else {
            this.f.ortsname.enable();
            if (this.stationsNamenSelection.length === 1) {
                this.f.stationsname.disable();
            } else {
                this.f.stationsname.enable();
            }
            if (this.f.stationsname.value === null || this.trafoFeldNamenSelection.length < 2) {
                this.f.trafoFeldName.disable();
            } else {
                this.f.trafoFeldName.enable();
            }
        }
    }

    /* Start: Methods concerning the three Netzzuordnung drop downs */

    // if a new Netzzuordnung.ortsname is selected, clear the other fields and load the available stationsnamen
    // if called from the template, set deleteCurrentNetzzuordnung to true, otherwise false, since we want to keep the
    // current Netzzuordnung when initializing the form for the very first time
    public async handleOrtsnameChanged(neuerOrtsname: string) {
        this.f.ortsname.setValue(neuerOrtsname);
        this.f.stationsname.setValue(null);
        this.resetTrafoFeldnameAndSelectedNetzzuordnung();
        await this.fillStationsnamen(neuerOrtsname);
        if (this.stationsNamenSelection.length === 1) {
            await this.handleStationsnameChanged(this.stationsNamenSelection[0]);
        }
        this.updateDisabledStates();
    }

    // if a new Netzzuordnung.stationsname is selected, clear the trafoFeldname field and load all available items
    public async handleStationsnameChanged(neuerStationsname: string) {
        this.f.stationsname.setValue(neuerStationsname);
        this.resetTrafoFeldnameAndSelectedNetzzuordnung();
        await this.fillTrafoFeldnamen(this.f.ortsname.value, neuerStationsname);
        if (this.trafoFeldNamenSelection.length === 0) {
            await this.handleTrafoFeldNameChanged('');
        } else if (this.trafoFeldNamenSelection.length === 1) {
            await this.handleTrafoFeldNameChanged(this.trafoFeldNamenSelection[0]);
        }
        this.updateDisabledStates();
    }

    // if a TrafoFeldname is selected, load the entity and assign it to the Erzeugungsanlage
    public async handleTrafoFeldNameChanged(neueTrafoFeldname: string) {
        this.f.trafoFeldName.setValue(neueTrafoFeldname);
        this.selectedNetzzuordnung = await lastValueFrom(
            this.netzzuordnungService.getFirstNetzzuordnung(
                this.getProperStadtwerkeId(),
                this.f.ortsname.value,
                this.f.stationsname.value,
                neueTrafoFeldname,
            ),
        );
        this.updateDisabledStates();
    }

    // assemebles a description of available details of this Netzzuordnung
    public assembleDescription(netzzuordnung: Netzzuordnung) {
        let name = netzzuordnung.ortsname + ', ' + netzzuordnung.stationsname;
        if (netzzuordnung.trafoFeldName) {
            name = name + ', ' + netzzuordnung.trafoFeldName;
        }
        return name;
    }

    // initializes the three dependent fields of the Netzzuordnung
    private async initializeNetzzuordnung() {
        await this.fillOrtsnamen();
        await this.handleOrtsnameChanged(this.recentNetzzuordnung.ortsname);
        await this.handleStationsnameChanged(this.recentNetzzuordnung.stationsname);
        await this.handleTrafoFeldNameChanged(this.recentNetzzuordnung.trafoFeldName);
        this.netzzuordnungInitialized$.next();
    }

    private resetTrafoFeldnameAndSelectedNetzzuordnung() {
        this.f.trafoFeldName.setValue(null);
        this.selectedNetzzuordnung = null;
    }

    // fills the drop down list of Netzzuordnung.Ortsname with available items
    private async fillOrtsnamen() {
        this.ortsnamenSelection = await lastValueFrom(this.netzzuordnungService.getOrtsnamen(this.getProperStadtwerkeId()));
    }

    // fills the drop down list of Netzzuordnung.Stationsname with available items depending on the chose ortsname
    private async fillStationsnamen(ortsname: string) {
        this.stationsNamenSelection = await lastValueFrom(this.netzzuordnungService.getStationsnamen(
            this.getProperStadtwerkeId(),
            ortsname,
        ));
    }

    // fills the drop down list of Netzzuordnung.Stationsname with available items depending on the chose ortsname and stationsname
    private async fillTrafoFeldnamen(ortsname: string, stationsname: string) {
        this.trafoFeldNamenSelection = await lastValueFrom(this.netzzuordnungService.getTrafoFeldNamen(
            this.getProperStadtwerkeId(),
            ortsname,
            stationsname,
        ));
    }

    // when the user is WESTNETZ, there is no stadtwerkeId passed via the @Input. In that case, however,
    // there must be a valid Netzzuordnung already present and thus, the stadtwerkeId can be obtained

    // via the related Netzbetreiber of the Netzzuordnung
    private getProperStadtwerkeId(): string {
        if (this.stadtwerkeId) {
            return this.stadtwerkeId;
        } else {
            if (this.recentNetzzuordnung && this.recentNetzzuordnung.netzbetreiber) {
                return this.recentNetzzuordnung.netzbetreiber.stadtwerkeId;
            } else {
                return '';
            }
        }
    }

}
